import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';

import * as fromCarriers from '@app/pages/delivery-service/store/reducers/index';
import { CarriersActions } from '@app/pages/delivery-service/store/actions';
import { Carrier } from '@app/services/carriers/carrier.model';
import { ShopService } from '@app/services/shops/shop.service';
import { takeUntil } from 'rxjs/operators';
import { SideDrawerComponent } from '../side-drawer.component/side-drawer.component';
import { CarriersService, FEDEX_VERIFICATION_OPTION_FIELD, FEDEX_DEFAULT_VERIFICATION_METHOD } from '../../services';
import { DeleteCarrierDialog } from '../delete-carrier.dialog/delete-carrier.dialog';
import { ErrorResponseDialog } from '../error-response.dialog/error-response.dialog';
import { FedExVerificationDialog } from '../fedex-verification.dialog/fedex-verification.dialog';
import { isEditingDisabledCarrier } from '@app/pages/checkout-app/utils/carrier.utils';

export enum DROP_DOWN_ACTIONS {
  EDIT_CARRIER = 'editCarrier',
  DELETE_CARRIER = 'deleteCarrier',
  DUPLICATE_CARRIER = 'duplicateCarrier',
  COMPLETE_VERIFICATION = 'completeVerification',
}

@Component({
  selector: 'app-page-list',
  templateUrl: './main-list-carrier.component.html',
  styleUrls: ['./main-list-carrier.component.scss'],
  standalone: false,
})
export class PageListComponent implements OnInit, OnDestroy {
  @ViewChild('drawer') drawer;
  @ViewChild(SideDrawerComponent) sideDrawer: SideDrawerComponent;

  positionTop = 54;
  assignSidebarHeight = 'calc(100vh - 54px)';
  currentShopId: number;
  isLoaded: boolean = false;
  currentCarrier: Carrier = undefined;
  notificationState: string = 'out';
  isCreatingCarrier: boolean = false;
  isOnTrialPlan: boolean = false;

  carrierDeleteErrorMessage$ = this.carriersStore.select(fromCarriers.selectErrorMessageDeleteCarrier);
  carriers$ = this.carriersStore.select(fromCarriers.selectCarriers);
  private ngUnsubscribe$ = new Subject<any>();

  constructor(
    public readonly dialog: MatDialog,
    private readonly carriersStore: Store<fromCarriers.State>,
    private readonly actions$: Actions,
    private readonly shopsService: ShopService,
    private readonly carriersService: CarriersService,
  ) {
    this.initCurrentCustomerId();
    this.initIsOnTrialPlan();
    this.detectLoadSuccess();
    this.detectDeleteCarrierFailure();
    this.subscribeOnCreateCarrierSuccess();
    this.subscribeOnEditCarrierSuccess();
  }

  ngOnInit(): void {
    this.loadCarriers();
    this.loadCarriersTypesConfig();
  }

  detectLoadSuccess(): void {
    this.isLoaded = true;
    this.actions$
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        ofType(CarriersActions.loadCarriersByShopSuccess, CarriersActions.loadCarriersByShopFailure),
      )
      .subscribe(() => {
        this.isLoaded = false;
      });
  }

  loadCarriersTypesConfig() {
    this.carriersStore.dispatch(CarriersActions.loadAllCarrierTypes());
  }

  detectDeleteCarrierFailure(): void {
    this.actions$
      .pipe(takeUntil(this.ngUnsubscribe$), ofType(CarriersActions.deleteCarrierFailure))
      .subscribe((res) => {
        this.dialog.open(ErrorResponseDialog, {
          panelClass: 'carrier-action-error',
          data: {
            title: 'Unable to delete delivery service',
            message: res.errorMessage,
          },
        });
      });
  }

  openAddNewCarrierWindow(): void {
    this.notificationState = 'out';
    this.sideDrawer.initAddNewCarrier();
    this.drawer.toggle();
  }

  changeActiveCarrier(checked, carrier: Carrier) {
    const isEditObject = { ...carrier, isActive: checked };
    this.carriersService.editCarrier(isEditObject, this.currentShopId);
  }

  onDropDownAction({ event, currentCarrier }) {
    if (event == DROP_DOWN_ACTIONS.DELETE_CARRIER) {
      this.openDeleteCarrierDialog(currentCarrier);
    } else if (event == DROP_DOWN_ACTIONS.COMPLETE_VERIFICATION) {
      this.openVerificationDialog(currentCarrier);
    } else {
      this.sideDrawer.onDropDownAction(event, currentCarrier);
    }
  }

  openDeleteCarrierDialog(currentCarrier): void {
    const deleteCarrierDialog = this.dialog.open(DeleteCarrierDialog, {
      data: {
        carrierName: currentCarrier.name,
      },
      autoFocus: false,
      panelClass: 'delete-carrier-dialog',
    });

    deleteCarrierDialog.componentInstance.onDelete.subscribe(() => {
      this.carriersStore.dispatch(
        CarriersActions.deleteCarrier({
          carrierId: currentCarrier.id,
          shopId: this.currentShopId,
        }),
      );
    });
  }

  ngOnDestroy(): void {
    this?.ngUnsubscribe();
  }

  private subscribeOnCreateCarrierSuccess() {
    this.actions$
      .pipe(ofType(CarriersActions.addNewCarrierSuccess), takeUntil(this.ngUnsubscribe$))
      .subscribe((payload) => {
        this.loadCarriers();
        this.isCreatingCarrier = false;
        this.drawer.close();
        this.notificationState = 'in';
        setTimeout(() => (this.notificationState = 'out'), 6240);

        if (payload.requiresVerification && payload.carrier) {
          this.openVerificationDialog(payload.carrier);
        } else if (this.carriersService.isCarrierRequiresExternalAuthentication(payload.carrier)) {
          this.carriersService.startCarrierExternalAuthentication(payload.carrier.id, this.currentShopId);
        }
      });
  }

  private subscribeOnEditCarrierSuccess() {
    this.actions$
      .pipe(ofType(CarriersActions.editCarrierSuccess), takeUntil(this.ngUnsubscribe$))
      .subscribe((payload) => {
        if (payload.requiresVerification && payload.carrier) {
          this.openVerificationDialog(payload.carrier);
        } else if (payload.metadata?.reauthenticate) {
          this.carriersService.startCarrierExternalAuthentication(payload.carrier.id, this.currentShopId);
        }
      });
  }

  private initCurrentCustomerId(): void {
    this.currentShopId = this.shopsService.shopId;
  }

  private initIsOnTrialPlan(): void {
    this.shopsService.shop$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((currentShop) => (this.isOnTrialPlan = currentShop?.isOnTrialPlan));
  }

  private loadCarriers(): void {
    this.carriersStore.dispatch(CarriersActions.loadCarriersByShop({ shopID: this.currentShopId }));
  }

  private ngUnsubscribe() {
    this.ngUnsubscribe$?.next(true);
    this.ngUnsubscribe$?.complete();
  }

  getIsActiveTooltip(carrier: Carrier): string {
    const isActiveMetaDataField = carrier && carrier.metadata?.find((field) => field.fieldName === 'isActive');
    return isActiveMetaDataField ? isActiveMetaDataField['descriptionText'] : '';
  }

  getIsExternalAuthenticationRequiredTooltip(carrier: Carrier): string {
    return this.isCarrierRequiresExternalAuthentication(carrier) ? 'Unauthorized' : '';
  }

  isEditingEnabledForCarrier(carrier: Carrier) {
    return !isEditingDisabledCarrier(carrier);
  }

  isCarrierRequiresExternalAuthentication(carrier: Carrier) {
    return this.carriersService.isCarrierRequiresExternalAuthentication(carrier);
  }

  isCarrierRequiresVerification(carrier: Carrier): boolean {
    return this.carriersService.isCarrierRequiresVerification(carrier);
  }

  openVerificationDialog(carrier: Carrier): void {
    const verificationMethod =
      carrier.metadata?.find((f) => f.fieldName === FEDEX_VERIFICATION_OPTION_FIELD)?.default ||
      FEDEX_DEFAULT_VERIFICATION_METHOD;

    const dialogRef = this.dialog.open(FedExVerificationDialog, {
      data: { carrierId: carrier.id, verificationMethod },
      autoFocus: false,
      panelClass: ['fedex-verification-dialog', 'padding-0'],
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((result) => {
        if (result?.success) {
          this.loadCarriers();
        }
      });
  }
}
